import { compact, zip } from 'lodash'
import moment from 'moment/moment'

import { TagProps } from '../components/app/design/Tag'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { mergeFk } from '../utils'
import { currencyFloat } from '../utils/numbers'
import { BaseRecord } from './BaseRecord'
import { Booking } from './Booking'
import { Contract } from './Contract'
import { Estimate } from './Estimate'
import { Gift } from './Gift'
import { Line } from './Line'
import { Option } from './Option'
import { PSEvent } from './PSEvent'
import { Payment } from './Payment'
import { FKType } from './SQLBuilder'
import { Signature } from './Signature'
import { Source } from './Source'
import { Transactable } from './Transactable'
import { Transaction } from './Transaction'

type InvoiceConstructorArgs = ConstructorArgs<() => Invoice>

export class Invoice extends Transaction implements Transactable {
    readonly recordType = 'invoice'

    readonly iTransactable = true

    static readonly INVOICE_GRACE_PERIOD = 0
    static readonly INVOICE_TOTAL_LIMIT = 500000

    memo?: string
    number?: number // Integer
    event?: PSEvent // only set when invoicing an existing appointment (not stored)
    status?: string // only settable for new invoices [sentQuote, paid] and used to log already paid invoices

    allowSurcharge?: boolean
    maxPaymentCount?: number // Integer
    minPaymentPercent?: number // Integer
    limitSurcharge?: boolean // only set when super-admin sends a Pro payment via ACH (not stored - only used for preview)

    frequency?: string
    interval?: number // Integer
    instances?: number // Integer
    nextInvoiceDate?: moment.Moment
    billableDate?: string

    type?: string
    private dueDate?: moment.Moment
    markPaidDate?: moment.Moment

    private readonly _paidAmount?: number

    readonly lastPaymentDate?: moment.Moment
    readonly remainingBalance?: number
    readonly remainingPaymentCount?: number

    constructor(data?: Partial<InvoiceConstructorArgs>, params?: { type?: string }) {
        super(data)
        if (!data) {
            this.type = params?.type
            this.reminder = '1'
            this.channel = 'sms'
            this.date = moment()
            this.dueDate = moment()
            return
        }

        this.number = this.propertyFromIntArg(data.number)
        this.dueDate = this.propertyFromDateArg(data.due_date)
        this.memo = data.memo ?? undefined
        this.type = data.type ?? undefined
        this.allowSurcharge = data.allow_surcharge ?? false
        this.maxPaymentCount = this.propertyFromIntArg(data.max_payment_count)
        this.minPaymentPercent = this.propertyFromIntArg(data.min_payment_percent)
        this.billableDate = data.billable_date ?? undefined
        this.frequency = data.frequency ?? undefined
        this.interval = this.propertyFromIntArg(data.interval)
        this.instances = this.propertyFromIntArg(data.instances)
        this.nextInvoiceDate = this.propertyFromDateArg(data.next_invoice_date)
        this.markPaidDate = this.propertyFromTimestampArg(data.mark_paid_date)

        this.status =
            this.paidDate !== undefined
                ? 'paid'
                : this.cancelledDate !== undefined
                  ? 'canceled'
                  : 'sentQuote'

        this._paidAmount = this.propertyFromCurrencyArg(data.paid_amount)

        this.lastPaymentDate = this.propertyFromTimestampArg(data.last_payment_date)
        this.remainingBalance = this.propertyFromCurrencyArg(data.remaining_balance)
        this.remainingPaymentCount = this.propertyFromNumberArg(data.remaining_payment_count)
    }

    clone(overrides?: Partial<InvoiceConstructorArgs>): Invoice {
        return new Invoice({ ...this.serializeForClone(), ...overrides })
    }

    static initWithType(type: string): Invoice {
        return new Invoice(undefined, { type })
    }

    get allowAnyPayment(): string | undefined {
        return this.maxPaymentCount !== undefined
            ? `${this.maxPaymentCount}_${this.minPaymentPercent}`
            : undefined
    }

    get amount(): number | undefined {
        if (this.total !== undefined && this.discount && this.discount > 0)
            return this.total - this.discountAmount
        return this.total
    }

    get discountAmount(): number {
        if (this.discount && this.discount > 0) {
            let discount = this.discount
            const total = this.discountableTotal
            if (this.discountType === 'percent')
                discount = currencyFloat((this.discount * (total ?? 0)) / 100)
            else if (discount > total) discount = total
            return discount
        }
        return 0
    }

    get discountableTotal(): number {
        if (this.lines && this.lines.length > 0) {
            let total = 0
            for (const line of this.lines)
                if (line.discountable && line.total !== undefined) total += line.total
            return total
        }
        return this.total ?? 0
    }

    get surchargeAmount(): number {
        if (this.allowSurcharge && !this.markPaidDate) {
            let surchargeAmount =
                (this.taxAmount + (this.amount ?? 0)) * (Source.surchargeRate(this.source) ?? 0)
            if (
                this.source &&
                this.source.type === 'checking' &&
                this.limitSurcharge &&
                surchargeAmount > Payment.ACH_PROCESSING_FEE_LIMIT
            )
                surchargeAmount = Payment.ACH_PROCESSING_FEE_LIMIT
            return surchargeAmount
        }
        return 0
    }

    get taxableAmount(): number {
        const taxcode = this.taxcode
        if (this.lines && this.lines.length > 0 && taxcode) {
            let total = 0
            for (const line of this.lines)
                if (line.taxable && line.amount !== undefined) total += line.amount
            return total
        }
        return taxcode ? (this.amount ?? 0) : 0
    }

    get taxAmount(): number {
        if (this.tax && this.tax > 0) {
            const taxableTotal = this.taxableAmount
            return currencyFloat((this.tax * (taxableTotal ?? 0)) / 100)
        }
        return 0
    }

    get chargeAmount(): number {
        return (this.amount ?? 0) + this.taxAmount + this.gratuityAmount + this.surchargeAmount
    }

    get askingAmount(): number {
        return (this.amount ?? 0) + this.taxAmount
    }

    get statusString(): string {
        if (!this.id && this.status === 'paid') return 'Already paid'
        if (this.cancelledDate) return `Canceled ${this.cancelledDate.format('MMM D, YYYY')}`
        if (this.markPaidDate && this.paymeth)
            return `Marked paid ${this.markPaidDate.format('MMM D, YYYY')} - ${this.paymeth}`
        if (this.markPaidDate) return `Marked paid ${this.markPaidDate.format('MMM D, YYYY')}`
        if (this.paidDate) return `Paid ${this.paidDate.format('MMM D, YYYY')}`
        if (this.id && this.type === 'schedule')
            return this.nextInvoiceDate
                ? `Next invoice ${this.nextInvoiceDate.format('MMM D')}`
                : 'Completed schedule'
        if (this.id && this.sentDate && this.paidAmount)
            return `Paid ${this.paidAmount} (${this.remainingBalance} left)`
        if (this.id && this.sentDate && this.due_date?.isBefore(moment().startOf('day')))
            return `Sent ${this.createdDate!.format('MMM D, YYYY')} - past due`
        if (this.id && this.sentDate)
            return `Sent ${this.createdDate!.format('MMM D, YYYY')} - not paid`
        if (this.id) return `Saved ${this.createdDate!.format('MMM D, YYYY')} - not sent`
        return 'Requires payment'
    }

    get statuses(): {
        title: string
        date: moment.Moment | undefined
        intent: TagProps['intent']
    }[] {
        return compact([
            {
                title: 'Saved',
                date: this.createdDate,
                intent: 'default',
            },
            {
                title: 'Sent',
                date: this.sentDate,
                intent: 'info',
            },
            {
                title: 'Viewed',
                date: this.viewedDate,
                intent: 'warning',
            },
            {
                title: 'Paid',
                date: this.paidDate,
                intent: 'success',
            },
            this.cancelledDate !== undefined && {
                title: 'Canceled',
                date: this.cancelledDate,
                intent: 'danger',
            },
        ])
    }

    get statusTag(): { label: string; intent: TagProps['intent'] } | null {
        const lastActiveStatus = this.statuses.findLast((status) => status.date !== undefined)
        if (!lastActiveStatus) {
            return null
        }

        return { label: lastActiveStatus.title, intent: lastActiveStatus.intent }
    }

    syncTotal(): void {
        let total = 0
        for (const line of this.lines) {
            if (line.total) {
                total += line.total
            }
        }
        this.total = total
    }

    syncSalesTax(): void {
        let applyTax = false
        if (this.lines.length > 0) {
            for (const line of this.lines) {
                if (line.taxable) {
                    applyTax = true
                    break
                }
            }
        }
        if (applyTax && this.vendor?.allowSalesTax && !this.taxcode) {
            const taxcode = this.vendor.taxcode
            this.tax = taxcode ? taxcode.rate : undefined
            this.nexus = taxcode ? 'pro' : undefined
            this.taxcode = taxcode
        }
    }

    get paidAmount(): number | undefined {
        const paidAmount = this._paidAmount ?? 0
        if (this.markPaidDate) {
            let amount = this.amount !== undefined ? this.amount - paidAmount : 0
            if (this.gratuityAmount > 0) amount += this.gratuityAmount
            if (this.taxAmount > 0) amount += this.taxAmount
            return amount
        }
        return paidAmount > 0 ? paidAmount : undefined
    }

    get gratuityAmount(): number {
        if (this.gratuity && this.gratuity > 0) {
            let gratuity = this.gratuity
            if (this.gratuityType === 'percent')
                gratuity = this.amount ? currencyFloat((this.gratuity * this.amount) / 100) : 0
            return gratuity
        }
        return 0
    }

    get owedAmount(): number | undefined {
        const paidAmount = this._paidAmount ?? 0
        return this.paidDate || this.cancelledDate
            ? undefined
            : this.amount
              ? this.amount - paidAmount
              : undefined
    }

    get itemTotal(): number {
        let total = 0
        for (const line of this.lines) if (line.type === 'item' && line.total) total += line.total
        return total
    }

    get expenseTotal(): number {
        let total = 0
        for (const line of this.lines)
            if (line.type === 'expense' && line.total) total += line.total
        return total
    }

    get bookingTotal(): number {
        let total = 0
        for (const line of this.lines)
            if (line.type === 'booking' && line.total) total += line.total
        return total
    }

    get itemAmount(): number {
        let total = 0
        for (const line of this.lines) if (line.type === 'item' && line.amount) total += line.amount
        return total
    }

    get expenseAmount(): number {
        let total = 0
        for (const line of this.lines)
            if (line.type === 'expense' && line.amount) total += line.amount
        return total
    }

    get bookingAmount(): number {
        let total = 0
        for (const line of this.lines)
            if (line.type === 'booking' && line.amount) total += line.amount
        return total
    }

    get jobCount(): number {
        let count = 0
        for (const line of this.lines) {
            if (line.type === 'job') {
                count++
            }
        }
        return count
    }

    get itemCount(): number {
        let count = 0
        for (const line of this.lines) {
            if (line.type === 'item') {
                count++
            }
        }
        return count
    }

    get expenseCount(): number {
        let count = 0
        for (const line of this.lines) {
            if (line.type === 'expense') {
                count++
            }
        }
        return count
    }

    get bookingCount(): number {
        let count = 0
        for (const line of this.lines) {
            if (line.type === 'booking') {
                count++
            }
        }
        return count
    }

    get description(): string {
        return `${this.total} . ${this.number}`
    }

    static reminderStrings(): Option[] {
        const strings: Option[] = []
        strings.push(new Option(undefined, { id: '1', label: 'Every day' }))
        strings.push(new Option(undefined, { id: '3', label: 'Every 3 days' }))
        strings.push(new Option(undefined, { id: '7', label: 'Once a week' }))
        strings.push(new Option(undefined, { id: '14', label: 'Every 2 weeks' }))
        strings.push(new Option(undefined, { id: '30', label: 'Every 30 days' }))
        strings.push(new Option(undefined, { id: '0', label: "Don't remind" }))
        return strings
    }

    static billableDateStrings(): Option[] {
        const strings: Option[] = []
        strings.push(new Option(undefined, { id: '', label: 'Not applicable' }))
        strings.push(new Option(undefined, { id: 'today', label: 'Past unpaid jobs' }))
        strings.push(new Option(undefined, { id: 'next_day', label: 'Future based on frequency' }))
        return strings
    }

    static allowAnyPaymentStrings(): Option[] {
        const strings: Option[] = []
        strings.push(new Option(undefined, { id: '', label: 'Single payment' }))
        strings.push(new Option(undefined, { id: '2_10', label: '2 payments (10% min)' }))
        strings.push(new Option(undefined, { id: '2_15', label: '2 payments (15% min)' }))
        strings.push(new Option(undefined, { id: '2_20', label: '2 payments (20% min)' }))
        strings.push(new Option(undefined, { id: '2_25', label: '2 payments (25% min)' }))
        strings.push(new Option(undefined, { id: '2_30', label: '2 payments (30% min)' }))
        strings.push(new Option(undefined, { id: '2_40', label: '2 payments (40% min)' }))
        strings.push(new Option(undefined, { id: '2_50', label: '2 payments (50% min)' }))
        strings.push(new Option(undefined, { id: '4_25', label: '4 payments (25% min)' }))
        return strings
    }

    get due_date() {
        return this.dueDate
    }

    set due_date(dueDate: moment.Moment | undefined) {
        this.dueDate = dueDate
    }

    get date() {
        return this._date
    }

    set date(date: moment.Moment | undefined) {
        this._date = date
        if (date && (this.due_date === null || date.isAfter(this.due_date)))
            this.due_date = date;
    }

    set allowAnyPayment(allowAnyPayment: string | undefined) {
        const parts = allowAnyPayment?.split('_') ?? []
        this.maxPaymentCount = parts.length > 0 ? parseInt(parts[0]) : undefined
        this.minPaymentPercent = parts.length > 0 ? parseInt(parts[1]) : undefined
    }

    get lines(): Line[] {
        return super.lines
    }

    set lines(lines: Line[]) {
        super.lines = lines
        for (const line of this.lines) {
            line.trandoc = this
        }
    }

    hasChangesFrom(otherInvoice: Invoice): boolean {
        if (BaseRecord.hasChanges(this, otherInvoice)) {
            return true
        }

        for (const [lhsLine, rhsLine] of zip(this.lines, otherInvoice.lines)) {
            if (lhsLine === undefined || rhsLine === undefined) {
                return true
            }
            if (lhsLine.hasChangesFrom(rhsLine)) {
                return true
            }
        }

        return false
    }

    deserialize(data: any) {
        super.deserialize(data)

        if (data.source) this.source = BaseRecord.fromSerializer(data.source, Source)
        if (data.booking) this.booking = BaseRecord.fromSerializer(data.booking, Booking)
        if (data.estimate) this.estimate = BaseRecord.fromSerializer(data.estimate, Estimate)
        if (data.gift) this.gift = BaseRecord.fromSerializer(data.gift, Gift)
        if (data.event) this.event = BaseRecord.fromSerializer(data.event, PSEvent)
        if (data.contract) this.contract = BaseRecord.fromSerializer(data.contract, Contract)
        if (data.signature) this.signature = BaseRecord.fromSerializer(data.signature, Signature)
        if (data.schedule) this.schedule = BaseRecord.fromSerializer(data.schedule, Invoice)
        if (data.date) this.date = moment(data.date)
        if (data.nextInvoiceDate) this.nextInvoiceDate = moment(data.nextInvoiceDate)
        if (data.due_date) this.due_date = moment(data.due_date)
        if (data.markPaidDate) this.markPaidDate = moment(data.markPaidDate)
        if (data.lastModifiedDate) this.lastModifiedDate = moment(data.lastModifiedDate)
    }

    static fk: FKType = mergeFk(Transaction.fk, {
        sources: ['source'],
        bookings: ['booking'],
        estimates: ['estimate'],
        gifts: ['gift'],
        contracts: ['contract'],
        signatures: ['signature'],
        invoices: ['schedule'],
        items: ['lines'],
        columnExtractors: {
            lines: 'item',
        },
    })

    static dataType(): DataType {
        return 'invoices'
    }

    jsonObject(): any {
        const dict = super.jsonObject()
        dict['discount'] = this.discount ?? ''
        dict['discount_type'] = this.discount && this.discountType ? this.discountType : ''
        dict['gratuity'] = this.gratuity ?? ''
        dict['gratuity_type'] = this.gratuity && this.gratuityType ? this.gratuityType : ''
        dict['date'] = this.date?.format('YYYY-MM-DD')
        dict['due_date'] = this.due_date?.format('YYYY-MM-DD')
        if (this.booking) dict['booking'] = this.booking.id
        if (this.event) dict['event'] = this.event.id
        if (!this.id) dict['status'] = this.status
        dict['source'] = this.source?.id ?? ''
        dict['gift'] = this.gift?.id ?? ''
        dict['estimate'] = this.estimate?.id ?? ''
        dict['contract'] = this.contract?.id ?? ''
        dict['memo'] = this.memo ?? ''
        dict['allow_surcharge'] = this.allowSurcharge
        dict['max_payment_count'] = this.maxPaymentCount
        dict['min_payment_percent'] = this.minPaymentPercent
        dict['billable_date'] = this.billableDate ?? ''
        dict['frequency'] = this.frequency ?? ''
        dict['interval'] = this.interval && this.interval !== 0 ? this.interval : ''
        dict['instances'] = this.instances && this.instances !== 0 ? this.instances : ''
        if (this.id && this.type === 'schedule')
            dict['next_invoice_date'] = this.nextInvoiceDate?.format('YYYY-MM-DD')
        const jLines = this.lines.map((line) => line.jsonObject())
        dict['lines'] = jLines
        return dict
    }

    get source(): Source | undefined {
        return this.getForeignRecord('source', Source)
    }
    set source(source: Source | undefined) {
        this.setForeignRecord('source', source)
    }
    get schedule(): Invoice | undefined {
        return this.getForeignRecord('schedule', Invoice)
    }
    set schedule(schedule: Invoice | undefined) {
        this.setForeignRecord('schedule', schedule)
    }
    get booking(): Booking | undefined {
        return this.getForeignRecord('booking', Booking)
    }
    set booking(booking: Booking | undefined) {
        this.setForeignRecord('booking', booking)
    }
    get estimate(): Estimate | undefined {
        return this.getForeignRecord('estimate', Estimate)
    }
    set estimate(estimate: Estimate | undefined) {
        this.setForeignRecord('estimate', estimate)
    }
    get gift(): Gift | undefined {
        return this.getForeignRecord('gift', Gift)
    }
    set gift(gift: Gift | undefined) {
        this.setForeignRecord('gift', gift)
    }
}
